import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { BURL } from "../../services/base";

import CustomButton from "../CustomButton";
import BankNoteIcon from "../Icons/BankNoteIcon";
import RightArrIcon from "../Icons/RightArrIcon";

import { useDispatch } from "react-redux";
import { setInvestments } from "../../slices/userSlice";
import CurrencyFormat from "react-currency-format";
import { formatNum } from "../../services/base";
import moment from "moment";

const DownSizeInvestment = ({ open, setOpen, investment: inv }) => {
    const [inProgress, setInProgress] = useState(false);
    const dispatch = useDispatch();
    const [err, setErr] = useState(null);

    const [state, setState] = useState({
        ...inv,
        tenure: inv.rem_days,
        new_principal: 0,
        new_maturity_interest: inv.maturity_interest,
        downsize_amount: 0,
        termination_interest_amount: 0,
        amount_after_termination: 0,
        percent_for_calc: 0,
        amount_calc: 0,
        interest_charge: inv.interest_charge || 0,
        loader: false,
        market: inv.money_market,
        part_liquidate_with_interest: inv.part_liquidate_with_interest,
        primary_principal: inv.principal,
        min_tenure: Math.max(parseInt(inv.min_tenure) - parseInt(inv.rem_days)),
        part_liquidation_date: new Date(),
        date_interest: inv.date_interest,
        rate: inv.rate,
        wht_status: inv.wht_status,
        maturity_date: inv.maturity_date,
    });

    useEffect(() => {
        updateData();
    }, [
        state.downsize_amount,
        state.wht_status,
        state.rate,
        state.part_liquidation_date,
        state.interest_charge,
    ]);

    const handlePartLiquidate = (e) => {
        if (!e.value) {
            setErr("Amount is required");
            return;
        } else if (Number(e.value) < 1) {
            setErr(`Amount can't be less than 1`);
            return;
        } else if (Number(e.value) > inv.principal) {
            setErr(`Insufficient balance`);
            return;
        } else {
            setErr(null);
        }
        setState((prev) => ({
            ...prev,
            downsize_amount: e.floatValue || 0,
        }));
    };

    const calculateTenure = () => {
        const today = moment();
        const maturityDate = moment(state.maturity_date, "DD/MM/YYYY").endOf(
            "day"
        );
        setState((prev) => ({
            ...prev,
            tenure: Math.abs(maturityDate.diff(today, "days")),
        }));
    };

    const updateData = () => {
        if (state.maturity_date && state.downsize_amount !== undefined) {
            calculateTenure();
        }

        const tenure = state.tenure || state.rem_days;
        let new_principal = 0;
        let new_maturity_interest = 0;

        const principal =
            parseFloat(state.primary_principal) -
            parseFloat(state.downsize_amount);
        const date_interest = state.date_interest;

        const percent_for_calc = (
            (parseFloat(state.downsize_amount) * 100) /
            state.primary_principal
        ).toFixed(2);

        const amount_calc = ((percent_for_calc / 100) * date_interest).toFixed(
            2
        );

        new_principal = principal;

        if (
            state.market === 0 ||
            (state.market === 1 && state.part_liquidate_with_interest === 1)
        ) {
            const ppenalty = isNaN(state.interest_charge)
                ? 0
                : state.interest_charge;
            const termination_interest_amount = (amount_calc * ppenalty) / 100;

            new_principal += date_interest - amount_calc;

            const amount_after_termination =
                amount_calc - termination_interest_amount;

            const part1 = (new_principal * state.rate * tenure) / 36500;
            const part2 = part1 * 0.1;

            new_maturity_interest =
                state.wht_status === 1 ? part1 - part2 : part1;

            if (
                state.downsize_amount === 0 &&
                inv.wht_status === state.wht_status
            ) {
                new_maturity_interest = state.maturity_interest;
            }

            setState((prev) => ({
                ...prev,
                tenure,
                new_principal,
                new_maturity_interest,
                termination_interest_amount,
                amount_after_termination,
                percent_for_calc,
                amount_calc,
            }));
        } else {
            setState((prev) => ({
                ...prev,
                new_principal,
            }));
        }
    };

    const successRes = () => {
        setOpen(false);
        Swal.fire({
            title: "Downsize Investment Successful",
            text: "Thank you for Investing with us",
            icon: "success",
            confirmButtonText: "Continue",
        });
    };

    const errorRes = (res) => {
        Swal.fire({
            title: "Can't Downsize Investment",
            text: res,
            icon: "error",
            confirmButtonText: "Okay",
        });
    };

    const onSubmit = (e) => {
        e.preventDefault();

        const residue_amount = state.date_interest - state.amount_calc;
        const payload = {
            id: state.id,
            customer_id: inv.customer_id,
            new_principal: state.new_principal,
            tenure: state.tenure ?? 0,
            wht_status: state.wht_status,
            downsize_amount: parseFloat(state.downsize_amount),
            termination_interest_amount: parseFloat(
                state.termination_interest_amount
            ),
            amount_after_termination: parseFloat(
                state.amount_after_termination
            ),
            residue_amount,
            new_maturity_interest: state.new_maturity_interest ?? 0,
            active_date: new Date(),
        };

        setInProgress(true);
        axios
            .post(`${BURL}down-size-investment`, payload)
            .then((res) => {
                if (!res?.data?.success) throw new Error(res);
                setInProgress(false);

                axios
                    .get(`${BURL}get-investments`)
                    .then((res) => {
                        if (!res?.data?.success) throw new Error(res);
                        setState((prev) => ({
                            ...prev,
                            downsize_amount: 0,
                        }));
                        dispatch(setInvestments(res?.data?.message));
                        setTimeout(() => {
                            window.location.reload();
                        }, 1000);
                    })
                    .catch((err) => {
                        toast.error("Can't update investment list");
                        setInProgress(false);
                    });
                successRes();
            })
            .catch((err) => {
                setInProgress(false);
                if (err?.response?.data) {
                    errorRes(err?.response?.data?.message);
                } else {
                    errorRes("An error occured, please try again.");
                }
            });
    };

    return (
        <Dialog
            fullWidth
            maxWidth="sm"
            open={open}
            PaperProps={{
                sx: { maxWidth: 500, borderRadius: 4 },
                className: "bg-[#F7F7F7] dark:bg-trov-dark dark:text-white",
            }}
            onClose={() => {
                setOpen(false);
                window.location.reload();
            }}
        >
            <DialogTitle className="mx-auto">
                <div className="space-y-4 pl-2 md:pl-4">
                    <div>
                        <h2 className="text-xl font-semibold text-white">
                            Part Liquidate Investment –{" "}
                            <span>{inv.ref_id}</span>
                        </h2>
                        <p className="text-sm text-gray-400 mt-1">
                            {inv.product_name}
                            {state.market === 1 && (
                                <small className="ml-2 text-xs text-black font-normal  px-1 py-0.5 rounded bg-[#E1FFE599]">
                                    Money Market
                                </small>
                            )}
                        </p>
                    </div>

                    {/* Investment Info Grid (2x2 layout) */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4 text-sm text-gray-300">
                        {/* Row 1 */}
                        <div className="flex flex-col">
                            <span className="text-gray-400">Cash Balance</span>
                            <span className="text-white font-medium text-base">
                                ₦{formatNum(inv.cash_balance)}
                            </span>
                        </div>

                        <div className="flex flex-col">
                            <span className="text-gray-400">
                                Current Principal
                            </span>
                            <span className="text-white font-medium text-base">
                                ₦{formatNum(inv.principal)}
                            </span>
                        </div>

                        {(state.market === 0 ||
                            (state.market === 1 &&
                                state.part_liquidate_with_interest === 0)) && (
                            <>
                                <div className="flex flex-col">
                                    <span className="text-gray-400">
                                        Accrued Interest
                                    </span>
                                    <span className="text-white font-medium text-base">
                                        ₦{formatNum(inv.date_interest)}
                                    </span>
                                </div>
                            </>
                        )}
                        <div className="flex flex-col">
                            <span className="text-gray-400">Rate</span>
                            <span className="text-white font-medium text-base">
                                {formatNum(inv.rate)}%
                            </span>
                        </div>
                    </div>
                </div>
            </DialogTitle>

            <form onSubmit={onSubmit}>
                <DialogContent>
                    <DialogContentText className="">
                        <div className="w-full block dark:text-white max-w-[26rem] mx-auto">
                            <label className="ml-2 text-xs font-bold">
                                Part Liquidate Amount
                            </label>
                            <div className="flex items-center relative flex-wrap">
                                <span className="absolute left-3">
                                    <BankNoteIcon />
                                </span>
                                <CurrencyFormat
                                    autoFocus
                                    thousandSeparator={true}
                                    prefix="₦ "
                                    onValueChange={handlePartLiquidate}
                                    value={state.downsize_amount}
                                    placeholder="Enter Amount"
                                    className={`w-full max-w-md border border-black dark:border-white rounded-full h-9 pr-4 pl-9 outline-none bg-transparent focus:border-primary transition text-sm ${
                                        err &&
                                        "border-red-500 dark:border-red-500"
                                    }`}
                                />
                            </div>
                            {err && (
                                <small className="absolute block ml-2 text-red-500">
                                    {err}
                                </small>
                            )}
                        </div>
                    </DialogContentText>

                    <div className="p-6 max-w-md mx-auto">
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-gray-300">
                                    New Principal:
                                </span>
                                <span className="font-medium">
                                    {`₦${formatNum(state.new_principal)}`}
                                </span>
                            </div>

                            {(state.market === 0 ||
                                (state.market === 1 &&
                                    state.part_liquidate_with_interest ===
                                        1)) && (
                                <div className="flex justify-between">
                                    <span className="text-gray-300">
                                        Amount after termination:
                                    </span>
                                    <span className="font-medium">
                                        {`₦${formatNum(
                                            state.amount_after_termination
                                        )}`}
                                    </span>
                                </div>
                            )}

                            {state.market === 0 && (
                                <>
                                    <div className="flex justify-between">
                                        <span className="text-gray-300">
                                            New Tenure (From Today):
                                        </span>
                                        <span className="font-medium">{`${state.tenure} Days`}</span>
                                    </div>

                                    <div className="flex justify-between">
                                        <span className="text-gray-300">
                                            New Maturity Interest:
                                        </span>
                                        <span className="font-medium">
                                            {`₦${formatNum(
                                                state.new_maturity_interest
                                            )}`}
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>

                        {(state.market === 0 ||
                            (state.market === 1 &&
                                state.part_liquidate_with_interest === 1)) && (
                            <div className="mt-5 pt-4 border-t border-gray-700">
                                <div className="flex justify-between">
                                    <span className="text-gray-300">
                                        Penalty Fee:
                                    </span>
                                    <span className="font-medium">
                                        {`₦${formatNum(
                                            state.termination_interest_amount
                                        )}`}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center" }}>
                    <CustomButton
                        dark
                        loading={inProgress}
                        disabled={
                            (state.market === 0 &&
                                (state.downsize_amount < 100 ||
                                    state.new_principal < 100)) ||
                            (state.market === 1 &&
                                (state.downsize_amount < 100 ||
                                    state.new_principal < 100))
                        }
                        className="w-full max-w-[26rem] my-4 justify-center mx-2"
                        type="submit"
                    >
                        <span>Continue</span>
                        {!inProgress && <RightArrIcon className="fill-white" />}
                    </CustomButton>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default DownSizeInvestment;
